import { computed, onMounted, ref, watch } from 'vue';
import { useI18n, usePlanLimits } from '@/composables';
import { useStore } from '@/store';

type RuleSettingsRule = {
  conditions: Record<string, unknown>;
  status?: string;
};

type CampaignRuleSettingsProps = {
  rule: RuleSettingsRule;
  active_name: string;
};

export const useCampaignRuleSettings = (props: CampaignRuleSettingsProps) => {
  const { t } = useI18n();
  const store = useStore();
  const planLimitsControl = usePlanLimits();

  const name = ref('');
  const conditions = ref<Rules.condition[]>([]);

  const conditionPlanLimits = (condition: string) =>
    planLimitsControl.conditionPlanLimits(condition);

  const defaultConditionsList = computed<Rules.condition[]>(() => [
    { name: t('Brand and Model'), condition: 'brand' },
    { name: t('Browser and Version'), condition: 'browser' },
    { name: t('Connection Type'), condition: 'connectType' },
    { name: t('Country'), condition: 'country' },
    { name: t('State / Region'), condition: 'state' },
    { name: t('City'), condition: 'city' },
    { name: t('Custom Token'), condition: 'customToken' },
    { name: t('Query Header'), condition: 'queryHeader' },
    { name: t('Custom 1'), condition: 'custom1' },
    { name: t('Custom 2'), condition: 'custom2' },
    { name: t('Custom 3'), condition: 'custom3' },
    { name: t('Custom 4'), condition: 'custom4' },
    { name: t('Custom 5'), condition: 'custom5' },
    { name: t('Custom 6'), condition: 'custom6' },
    { name: t('Custom 7'), condition: 'custom7' },
    { name: t('Custom 8'), condition: 'custom8' },
    { name: t('Custom 9'), condition: 'custom9' },
    { name: t('Custom 10'), condition: 'custom10' },
    { name: t('Day of Week'), condition: 'dayOfWeek' },
    { name: t('Device Type'), condition: 'deviceType' },
    { name: t('IP'), condition: 'ip' },
    { name: t('ISP'), condition: 'isp' },
    { name: t('Language'), condition: 'language' },
    { name: t('Mobile Carrier'), condition: 'mobileCarrier' },
    { name: t('OS and Version'), condition: 'os' },
    { name: t('Referrer'), condition: 'referrer' },
    { name: t('Referrer Domain'), condition: 'referrerDomain' },
    { name: t('Time of Day'), condition: 'timeOfDay' },
    { name: t('User Agent'), condition: 'userAgent' },
    { name: t('Uniqueness'), condition: 'uniqueness' },
    { name: t('Recurring Conversion'), condition: 'uniqOfferViewer' },
    { name: t('Bot Filter Marks'), condition: 'botMark' },
    { name: t('WebView'), condition: 'webViewMark' },
    { name: t('Crawler'), condition: 'crawler' },
    { name: t('Proxy'), condition: 'proxy' },
    { name: t('Visits Cap'), condition: 'visitCap' },
    { name: t('Suspicious Traffic (blacklisted IPs)'), condition: 'ipBlacklisted' },
    { name: t('Suspicious Traffic (data centres IPs)'), condition: 'ipDataCenter' },
    { name: t('Suspicious Traffic (Frequent Events)'), condition: 'ipWithFrequentEvents' },
    { name: t('Suspicious Traffic (Frequent Campaign Events)'), condition: 'ipWithFrequentCampaignEvents' },
    { name: t('External ID'), condition: 'externalId' },
  ]);

  const selected = computed(() =>
    conditions.value
      .filter((item) => item.selected)
      .map((item) => item.condition),
  );

  const isSelected = (type: string) => selected.value.includes(type);

  const menuList = computed(() =>
    conditions.value
      .filter((item) => !item.selected)
      .map<Bemob.ListItem>((item) => {
        const planLimits = conditionPlanLimits(item.condition);
        return {
          key: item.condition,
          title: item.name,
          ...(planLimits ? { planLimits } : {}),
          click: () => add(item),
        };
      }),
  );

  const isTimezoneAvailable = computed(
    () => isSelected('timeOfDay') || isSelected('dayOfWeek'),
  );

  const isClientHintsNoticeVisible = computed(() =>
    Object.keys(props.rule.conditions).some((item) =>
      ['os', 'browser', 'deviceType', 'brand'].includes(item),
    ),
  );

  const syncSelectedFromRule = (value: Record<string, unknown> | null | undefined) => {
    if (!value) {
      return;
    }

    const ruleKeys = Object.keys(value);
    conditions.value = conditions.value.map((item) => ({
      ...item,
      selected: ruleKeys.includes(item.condition),
    }));
  };

  const add = (item: Rules.condition) => {
    const index = conditions.value.indexOf(item);
    if (index !== -1) {
      conditions.value[index] = {
        ...conditions.value[index],
        selected: true,
      };
    }
  };

  const remove = (conditionName: string) => {
    conditions.value = conditions.value.map((item) => {
      if (item.selected && item.condition === conditionName) {
        return {
          ...item,
          selected: false,
        };
      }
      return item;
    });

    delete props.rule.conditions[conditionName];

    if (!isTimezoneAvailable.value && props.rule.conditions.timezone) {
      delete props.rule.conditions.timezone;
    }
  };

  const deselectAll = () => {
    conditions.value = conditions.value.map((item) => ({
      ...item,
      selected: false,
    }));
    props.rule.conditions = {};
  };

  watch(
    () => props.active_name,
    (value) => {
      name.value = value;

      setTimeout(() => {
        store.setLoading(false);
      }, 1000);
    },
  );

  watch(
    () => props.rule.conditions,
    (value) => {
      syncSelectedFromRule(value);
    },
    { deep: true },
  );

  onMounted(() => {
    conditions.value = defaultConditionsList.value.map((item) => ({ ...item }));
    syncSelectedFromRule(props.rule.conditions);
  });

  return {
    t,
    conditions,
    menuList,
    isTimezoneAvailable,
    isClientHintsNoticeVisible,
    isSelected,
    add,
    remove,
    deselectAll,
    selected,
  };
};
